import React, { useState, useMemo } from "react";
import { Card } from "../types";
import BinderView from "./BinderView"; // Import the BinderView component
import CardSearch from "./CardSearch";
import { doc, deleteDoc, collection, query, where, getDocs, writeBatch } from "firebase/firestore";
import { db } from "../firebase/db";
import { useAuth } from "../context/AuthContext";

const PAGE_SIZE = 8; // Number of cards per page

interface AssignCardModalProps {
  inventory: Card[];
  wishlist: Card[];
  onAssignFromInventory: (cardId: string) => void;
  onAssignFromSearch: (cardData: any) => void;
  onAssignFromWishlist: (cardData: any) => Promise<void>; // <-- Add this
  onClose: () => void;
}

const AssignCardModal: React.FC<AssignCardModalProps> = ({
  inventory,
  wishlist,
  onAssignFromInventory,
  onAssignFromSearch,
  onAssignFromWishlist,
  onClose,
}) => {
  const { user } = useAuth();
  const [mode, setMode] = useState<"inventory" | "search" | "wishlist">(
    "inventory"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSet, setSearchSet] = useState("");
  const [searchRarity, setSearchRarity] = useState("");
  const [searchSupertype, setSearchSupertype] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedBinder, setSelectedBinder] = useState<null | any>(null);
  const [pendingCard, setPendingCard] = useState<any | null>(null);
  const [showChoice, setShowChoice] = useState(false);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [wishlistPage, setWishlistPage] = useState(1);

  // New filter state for inventory/wishlist
  const [inventoryFilter, setInventoryFilter] = useState("");
  const [wishlistFilter, setWishlistFilter] = useState("");

  // Filtered lists
  const filteredInventory = inventoryFilter
    ? inventory.filter((card) =>
        card.name.toLowerCase().includes(inventoryFilter.toLowerCase())
      )
    : inventory;

  const filteredWishlist = wishlistFilter
    ? wishlist.filter((card) =>
        card.name.toLowerCase().includes(wishlistFilter.toLowerCase())
      )
    : wishlist;

  // Pagination helpers (use filtered lists)
  const paginatedInventory = filteredInventory.slice(
    (inventoryPage - 1) * PAGE_SIZE,
    inventoryPage * PAGE_SIZE
  );
  const paginatedWishlist = filteredWishlist.slice(
    (wishlistPage - 1) * PAGE_SIZE,
    wishlistPage * PAGE_SIZE
  );
  const inventoryTotalPages = Math.ceil(filteredInventory.length / PAGE_SIZE);
  const wishlistTotalPages = Math.ceil(filteredWishlist.length / PAGE_SIZE);

  // Real search function using the TCG API
  const searchCards = async (term: string) => {
    setLoading(true);
    setSearchError(null);
    try {
      // Ensure term is a string and wrap in quotes if it contains spaces
      let safeTerm = String(term || "").trim();
      if (safeTerm.includes(" ")) {
        safeTerm = `"${safeTerm}"`;
      }
      let queryUrl = `https://api.pokemontcg.io/v2/cards?q=name:${encodeURIComponent(safeTerm)}`;
      if (searchSet) {
        const setValue = searchSet.includes(" ") ? `"${searchSet}"` : searchSet;
        queryUrl += ` set.name:${encodeURIComponent(setValue)}`;
      }
      if (searchRarity) {
        const rarityValue = searchRarity.includes(" ") ? `"${searchRarity}"` : searchRarity;
        queryUrl += ` rarity:${encodeURIComponent(rarityValue)}`;
      }
      if (searchSupertype) queryUrl += ` supertype:${encodeURIComponent(searchSupertype)}`;
      const res = await fetch(queryUrl, {
        headers: { "X-Api-Key": process.env.REACT_APP_POKEMON_API_KEY || "" },
      });
      const data = await res.json();
      setSearchResults(data.data || []);
    } catch (err) {
      setSearchError("Search failed. Please try again.");
    }
    setLoading(false);
  };

  // Build a map of binderId -> binderName from all cards that have both
  const binderIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    [...inventory, ...wishlist].forEach((card: any) => {
      if (card.assignedBinderId && card.binderName) {
        map[card.assignedBinderId] = card.binderName;
      }
    });
    return map;
  }, [inventory, wishlist]);

  // Helper to get assignment info for a card
  function getAssignmentInfo(card: any) {
    if (card.assignedBinderId && card.assignedSlot) {
      const binderName = binderIdToName[card.assignedBinderId] || "Unknown Binder";
      const page = (card.assignedSlot.page ?? 0) + 1;
      const side = card.assignedSlot.side
        ? card.assignedSlot.side.charAt(0).toUpperCase() + card.assignedSlot.side.slice(1)
        : "Front";
      return `Assigned: ${binderName}, Page ${page} (${side})`;
    }
    return null;
  }

  const handleDeleteBinder = async () => {
    if (!user || !selectedBinder) return;

    // Clear assignments for all cards in inventory and wishlist assigned to this binder
    const inventoryRef = collection(db, "users", user.uid, "inventory");
    const inventoryQuery = query(inventoryRef, where("assignedBinderId", "==", selectedBinder.id));
    const inventorySnap = await getDocs(inventoryQuery);

    const wishlistRef = collection(db, "users", user.uid, "wishlist");
    const wishlistQuery = query(wishlistRef, where("assignedBinderId", "==", selectedBinder.id));
    const wishlistSnap = await getDocs(wishlistQuery);

    const batch = writeBatch(db);

    inventorySnap.forEach(docSnap => {
      batch.update(docSnap.ref, {
        assignedBinderId: null,
        binderName: null,
        assignedSlot: null,
      });
    });

    wishlistSnap.forEach(docSnap => {
      batch.update(docSnap.ref, {
        assignedBinderId: null,
        binderName: null,
        assignedSlot: null,
      });
    });

    await batch.commit();
    await deleteDoc(doc(db, "users", user.uid, "binders", selectedBinder.id));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg mx-2">
        <h2 className="text-lg font-bold mb-4">Assign Card to Slot</h2>
        <div className="flex gap-2 mb-4">
          <button
            className={`px-3 py-1 rounded ${
              mode === "inventory"
                ? "bg-indigo-500 text-white"
                : "bg-gray-200"
            }`}
            onClick={() => setMode("inventory")}
          >
            From Inventory
          </button>
          <button
            className={`px-3 py-1 rounded ${
              mode === "wishlist" ? "bg-yellow-400 text-white" : "bg-gray-200"
            }`}
            onClick={() => setMode("wishlist")}
          >
            From Wishlist
          </button>
          <button
            className={`px-3 py-1 rounded ${
              mode === "search" ? "bg-indigo-500 text-white" : "bg-gray-200"
            }`}
            onClick={() => setMode("search")}
          >
            Search & Add
          </button>
        </div>
        {mode === "inventory" ? (
          <div>
            <input
              type="text"
              className="input input-bordered w-full mb-2"
              placeholder="Filter inventory by name..."
              value={inventoryFilter}
              onChange={(e) => {
                setInventoryFilter(e.target.value);
                setInventoryPage(1); // Reset to first page on filter
              }}
            />
            <div className="grid grid-cols-2 gap-2 mb-4">
              {paginatedInventory.map((card) => (
                <button
                  key={card.id}
                  className="flex flex-col items-center border rounded p-2 hover:bg-indigo-50 relative"
                  onClick={() => onAssignFromInventory(card.id)}
                >
                  <img
                    src={card.image}
                    alt={card.name}
                    className="w-12 h-16 object-contain mb-1"
                  />
                  <span className="text-xs">{card.name}</span>
                  {getAssignmentInfo(card) && (
                    <span className="text-[10px] text-indigo-600 mt-1 text-center">
                      {getAssignmentInfo(card)}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {inventoryTotalPages > 1 && (
              <div className="flex justify-center gap-2 mb-2">
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-xs"
                  disabled={inventoryPage === 1}
                  onClick={() => setInventoryPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="text-xs flex items-center">
                  Page {inventoryPage} of {inventoryTotalPages}
                </span>
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-xs"
                  disabled={inventoryPage === inventoryTotalPages}
                  onClick={() => setInventoryPage((p) => Math.min(inventoryTotalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        ) : mode === "wishlist" ? (
          <div>
            <input
              type="text"
              className="input input-bordered w-full mb-2"
              placeholder="Filter wishlist by name..."
              value={wishlistFilter}
              onChange={(e) => {
                setWishlistFilter(e.target.value);
                setWishlistPage(1); // Reset to first page on filter
              }}
            />
            <div className="grid grid-cols-2 gap-2 mb-4">
              {paginatedWishlist.map((card) => (
                <button
                  key={card.id}
                  className="flex flex-col items-center border rounded p-2 hover:bg-yellow-50 relative"
                  onClick={() => onAssignFromInventory(card.id)}
                >
                  <img
                    src={card.image}
                    alt={card.name}
                    className="w-12 h-16 object-contain mb-1"
                  />
                  <span className="text-xs">{card.name}</span>
                  <span className="text-xxs text-yellow-600 font-bold">Wishlist</span>
                  {getAssignmentInfo(card) && (
                    <span className="text-[10px] text-indigo-600 mt-1 text-center">
                      {getAssignmentInfo(card)}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {wishlistTotalPages > 1 && (
              <div className="flex justify-center gap-2 mb-2">
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-xs"
                  disabled={wishlistPage === 1}
                  onClick={() => setWishlistPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <span className="text-xs flex items-center">
                  Page {wishlistPage} of {wishlistTotalPages}
                </span>
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-xs"
                  disabled={wishlistPage === wishlistTotalPages}
                  onClick={() => setWishlistPage((p) => Math.min(wishlistTotalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        ) : (
          // Only show CardSearch for "search" mode
          <CardSearch
            buttonLabel="Select"
            onCardSelect={card => {
              setPendingCard(card);
              setShowChoice(true);
            }}
          />
        )}
        <button
          className="mt-4 px-4 py-2 rounded bg-gray-200"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
      {selectedBinder && ( // Render the BinderView if a binder is selected
        <BinderView
          binder={selectedBinder}
          onClose={() => setSelectedBinder(null)}
          inventory={inventory}
          wishlist={wishlist}
        />
      )}
      {showChoice && pendingCard && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-xs mx-2 flex flex-col items-center">
            <h3 className="font-bold mb-2 text-center">Add card as...</h3>
            <img
              src={pendingCard.images?.small}
              alt={pendingCard.name}
              className="w-20 h-28 object-contain mb-2"
            />
            <div className="font-bold mb-2">{pendingCard.name}</div>
            <div className="flex gap-2">
              <button
                className="px-3 py-1 bg-indigo-500 text-white rounded"
                onClick={async () => {
                  // Add to inventory
                  const cardToSave = {
                    name: pendingCard.name,
                    set: pendingCard.set?.name || "",
                    number: pendingCard.number,
                    rarity: pendingCard.rarity || "",
                    image: pendingCard.images?.small || "",
                    tcgId: pendingCard.id,
                    tcgplayerUrl: pendingCard.tcgplayer?.url || "",
                    createdAt: new Date(),
                  };
                  await onAssignFromSearch(cardToSave); // This should add to inventory and assign to binder
                  setShowChoice(false);
                  setPendingCard(null);
                }}
              >
                Inventory
              </button>
              <button
                className="px-3 py-1 bg-yellow-400 text-white rounded"
                onClick={async () => {
                  const cardToSave = {
                    name: pendingCard.name,
                    set: pendingCard.set?.name || "",
                    number: pendingCard.number,
                    rarity: pendingCard.rarity || "",
                    image: pendingCard.images?.small || "",
                    tcgId: pendingCard.id,
                    tcgplayerUrl: pendingCard.tcgplayer?.url || "",
                    createdAt: new Date(),
                  };
                  await onAssignFromWishlist(cardToSave); // <-- Actually add to wishlist and assign
                  setShowChoice(false);
                  setPendingCard(null);
                }}
              >
                Wishlist
              </button>
            </div>
            <button
              className="mt-4 px-4 py-2 rounded bg-gray-200"
              onClick={() => {
                setShowChoice(false);
                setPendingCard(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignCardModal;